// Drift: katalog, priser, ledige tider og opsætning på Supabase (del af SupabaseApi – se supabase.ts).
// Bookingsiden får katalog, pris og tider fra edge-funktionen "booking", som regner med samme motor som testversionen.
// Kontoret retter katalog og opsætning direkte i tabellerne (RLS: har_evne) – det, der rører flere tabeller, via funktioner.
// Felterne tjekkes med samme regler som i testversionen (src/core/opsaetning.ts).
import type { SupabaseClient } from '@supabase/supabase-js';
import { ApiFejl, type Api, type Indstillinger } from './api';
import type { Arbejdstid, Formularfelt, Fravaer, FravaerAnmodning, Kategori, Kupon, Lukkedag, SaerligDag, Steamer, Tilvalg, Ydelse, YdelsePris, Zone } from '../../core/typer';
import { KOLONNER, fraDb, tilDb } from '../../core/raekker';
import { fletIndstillinger, standardIndstillinger } from '../../core/indstillinger';
import { standardKatalog } from '../../core/standardKatalog';
import { helligdage, iDag } from '../../core/dato';
import { rens } from '../../core/validering';
import * as O from '../../core/opsaetning';
import { SAELGER_KAN, STANDARD_ROLLE_EVNER, type RolleEvner } from '../../core/roller';
import { TOM_BRAND, normBrand, type SubBrand } from '../../core/brands';
import { normPartner, type Partner, type PartnerPakke } from '../../core/partnere';
import { normFastFri, type FastFri, type FastFriAnmodning } from '../../core/fastFri';

/** Det, katalogdelen bruger fra SupabaseApi. */
export type SbKontekst = {
  sb: SupabaseClient;
  k<R extends { data: unknown; error: unknown }>(p: PromiseLike<R>): Promise<R extends { error: null } ? R['data'] : never>;
  kraevKlient(): Promise<string>;
  funktion<T>(navn: string, body: Record<string, unknown>): Promise<T>;
  skift(): void;
  adresse(): { vaert: string; slug: string | null } | null;
};
type R = Record<string, unknown>;

export class SbKatalog {
  constructor(private readonly c: SbKontekst) {}

  // ---------- Bookingsiden (edge-funktionen "booking") ----------
  async kald<T>(handling: string, data: Record<string, unknown> = {}): Promise<T> {
    const a = this.c.adresse();
    if (!a) throw new ApiFejl('Adressen findes ikke. Tjek, at den er skrevet rigtigt.');
    return this.c.funktion<T>('booking', { vaert: a.vaert, slug: a.slug, handling, ...data });
  }
  // Kataloget (behandlinger, tilvalg, priser) bruges mange steder på samme side – fx bookingen og "Tilføj tilvalg". Det hentes
  // derfor kun én gang pr. minut pr. brand/partner og glemmes, når noget ændres (glemKatalog kaldes fra skift()).
  private katalogCache = new Map<string, { tid: number; p: ReturnType<Api['katalog']> }>();
  glemKatalog() { this.katalogCache.clear(); }
  katalog: Api['katalog'] = (brand = null, partner = null) => {
    const noegle = JSON.stringify([brand, partner]);
    const c = this.katalogCache.get(noegle);
    if (c && Date.now() - c.tid < 60_000) return c.p;
    const p = this.kald<Awaited<ReturnType<Api['katalog']>>>('katalog', { ...(brand ? { brand } : {}), ...(partner ? { partner } : {}) });
    this.katalogCache.set(noegle, { tid: Date.now(), p });
    p.catch(() => { if (this.katalogCache.get(noegle)?.p === p) this.katalogCache.delete(noegle); });
    return p;
  };
  partnerSide: Api['partnerSide'] = (slug) => this.kald('partner', { slug });
  prisOverslag: Api['prisOverslag'] = (a) => this.kald('pris', { biler: a.biler, kupon: a.kupon, medlem: a.medlem ?? null, dato: a.dato ?? null, betaling: a.betaling, partner: a.partner ?? null });
  medlemsTilbud: Api['medlemsTilbud'] = (a) => this.kald('medlem', { biltype_id: a.biltype_id, ydelse_id: a.ydelse_id, tilvalg: a.tilvalg ?? [] });
  ledigeDage: Api['ledigeDage'] = (a) => this.kald('dage', { fra: a.fra, dage: a.dage, postnr: a.postnr, biler: a.biler, partner: a.partner ?? null });
  ledigeTider: Api['ledigeTider'] = (a) => this.kald('tider', { dato: a.dato, postnr: a.postnr, biler: a.biler, partner: a.partner ?? null });
  liveBeskeder: Api['liveBeskeder'] = (a) => this.kald('live', { session: a.session, postnr: a.postnr ?? null, brand: a.brand ?? null });
  slaaNummerplade: Api['slaaNummerplade'] = (plade) => this.kald('nummerplade', { plade });
  // Opsætning › Integrationer: om nøglen (edge secret NUMMERPLADE_API_KEY) er sat, ved kun edge-funktionen; tallene ligger i databasen
  nummerpladeStatus: Api['nummerpladeStatus'] = async () => {
    const k = await this.c.kraevKlient();
    const [s, e] = await Promise.all([
      this.c.k(this.c.sb.rpc('nummerplade_status', { k })) as Promise<{ iDag: number; registerIDag: number }>,
      this.kald<{ satOp: boolean }>('nummerplade_status'),
    ]);
    return { satOp: e.satOp, test: false, iDag: s.iDag, registerIDag: s.registerIDag };
  };
  testNummerplade: Api['testNummerplade'] = (plade) => this.kald('nummerplade_test', { plade });
  opretBooking: Api['opretBooking'] = (bestilling) => this.kald('bestil', { bestilling });
  bekraeftelse: Api['bekraeftelse'] = (kode) => this.kald('bekraeftelse', { kode });
  // Kundens link til at vælge en ny tid (Mangler tid): tjekkes og regnes ud i edge-funktionen
  nyTidLink: Api['nyTidLink'] = (token) => this.kald('nytid', { token });
  nyTidDage: Api['nyTidDage'] = (token, fra, dage) => this.kald('nytid_dage', { token, fra, dage });
  nyTidTider: Api['nyTidTider'] = (token, dato) => this.kald('nytid_tider', { token, dato });
  vaelgNyTid: Api['vaelgNyTid'] = async (token, dato, start) => { const r = await this.kald<{ status: import('../../core/typer').BookingStatus }>('nytid_vaelg', { token, dato, start }); this.c.skift(); return r; };

  // ---------- Hjælpere ----------
  private async liste(tabel: keyof typeof KOLONNER, orden?: string): Promise<R[]> {
    const q = this.c.sb.from(tabel).select(KOLONNER[tabel]).eq('klient_id', await this.c.kraevKlient());
    return (await this.c.k(orden ? q.order(orden) : q)) as unknown as R[];
  }
  private async naesteSortering(tabel: 'kategorier' | 'tilvalg' | 'formularfelter', k: string): Promise<number> {
    const r = await this.c.k(this.c.sb.from(tabel).select('sortering').eq('klient_id', k).order('sortering', { ascending: false }).limit(1).maybeSingle());
    return ((r as { sortering: number } | null)?.sortering ?? 0) + 1;
  }
  private async gem(tabel: 'kategorier' | 'tilvalg' | 'formularfelter', k: string, raekke: R & { id: string; sortering: number }, ny: boolean) {
    const v = { ...raekke, klient_id: k, ...(ny ? { sortering: await this.naesteSortering(tabel, k) } : {}) };
    await this.c.k(this.c.sb.from(tabel).upsert(v, { onConflict: 'klient_id,id' }));
    this.c.skift();
  }
  private async slet(tabel: string, k: string, felt: string, vaerdi: string) {
    await this.c.k(this.c.sb.from(tabel).delete().eq('klient_id', k).eq(felt, vaerdi));
    this.c.skift();
  }
  private rpc(navn: string, args: Record<string, unknown>) { return this.c.k(this.c.sb.rpc(navn, args)); }

  /** Ny virksomhed: læg standardkataloget ind (kun hvis den ikke har et katalog endnu). Svarer true, hvis det blev lagt ind. */
  async saaKatalog(k: string): Promise<boolean> {
    return (await this.rpc('saa_katalog', { k, d: standardKatalog(iDag()) })) === true;
  }

  // ---------- Katalog ----------
  gemYdelse: Api['gemYdelse'] = async (y: Ydelse, priser: YdelsePris[]) => {
    const n = O.normYdelse(y, priser);
    await this.rpc('gem_ydelse', { k: await this.c.kraevKlient(), y: tilDb.ydelse(n.ydelse), priser: n.priser.map(({ biltype_id, pris, varighed }) => ({ biltype_id, pris, varighed })) });
    this.c.skift();
  };
  gemTilvalg: Api['gemTilvalg'] = async (t: Tilvalg) => { const k = await this.c.kraevKlient(); await this.gem('tilvalg', k, tilDb.tilvalg(O.normTilvalg(t)), !t.id); };
  gemKategori: Api['gemKategori'] = async (x: Kategori) => { const k = await this.c.kraevKlient(); const n = O.normKategori(x); await this.gem('kategorier', k, { id: n.id, navn: n.navn, sortering: n.sortering ?? 0 }, !x.id); };
  sletKategori: Api['sletKategori'] = async (id: string) => {
    const k = await this.c.kraevKlient();
    O.kategoriKanSlettes(id, (await this.c.k(this.c.sb.from('ydelser').select('kategori_id').eq('klient_id', k).eq('kategori_id', id))) as { kategori_id: string }[]);
    await this.slet('kategorier', k, 'id', id);
  };
  zoner: Api['zoner'] = async () => (await this.liste('zoner', 'navn')).map(fraDb.zone);
  gemZone: Api['gemZone'] = async (z: Zone, steamere?: string[]) => {
    await this.rpc('gem_zone', { k: await this.c.kraevKlient(), z: O.normZone(z), p_steamere: steamere ?? null });
    this.c.skift();
  };
  sletZone: Api['sletZone'] = async (id: string) => { await this.rpc('slet_zone', { k: await this.c.kraevKlient(), p_id: id }); this.c.skift(); };
  // Nyhedsbrevenes unikke koder (én pr. modtager, kuponer.nyhed) vises ikke her – de hører til kampagnen
  kuponer: Api['kuponer'] = async () => ((await this.c.k(this.c.sb.from('kuponer').select(KOLONNER.kuponer).eq('klient_id', await this.c.kraevKlient()).eq('nyhed', false))) as unknown as R[]).map(fraDb.kupon).sort((a, b) => b.oprettet.localeCompare(a.oprettet));
  gemKupon: Api['gemKupon'] = async (x: Kupon, ny = false) => {
    const k = await this.c.kraevKlient();
    const kode = rens(x.kode, 30).toUpperCase().replace(/\s/g, '');
    const findes = ny && !!(await this.c.k(this.c.sb.from('kuponer').select('kode').eq('klient_id', k).eq('kode', kode).maybeSingle()));
    const n = O.normKupon(x, ny, findes, iDag());
    const { brugt: _brugt, ...v } = tilDb.kupon(n);   // brugt tælles af serveren
    if (ny) await this.c.k(this.c.sb.from('kuponer').insert({ ...v, klient_id: k }));
    else {
      const r = await this.c.k(this.c.sb.from('kuponer').update(v).eq('klient_id', k).eq('kode', n.kode).select('kode'));
      if (!(r as unknown[]).length) throw new ApiFejl('Rabatkoden findes ikke længere.');
    }
    this.c.skift();
  };

  kuponForbrug: Api['kuponForbrug'] = async (fra: string) => {
    const k = await this.c.kraevKlient();
    const o = await this.c.k(this.c.sb.from('ordrer').select('id').eq('klient_id', k).not('kupon', 'is', null).gte('oprettet', fra)) as { id: number }[];
    if (!o.length) return { antal: 0, rabat: 0 };
    const b = await this.c.k(this.c.sb.from('bookinger').select('rabat').eq('klient_id', k).in('ordre_id', o.map((x) => x.id))) as { rabat: number | null }[];
    return { antal: o.length, rabat: b.reduce((s, x) => s + (x.rabat ?? 0), 0) };
  };

  // ---------- Formularfelter, indstillinger og lukkedage ----------
  gemFelt: Api['gemFelt'] = async (f: Formularfelt) => { const k = await this.c.kraevKlient(); await this.gem('formularfelter', k, tilDb.felt(O.normFelt(f)), !f.id); };
  sletFelt: Api['sletFelt'] = async (id: string) => this.slet('formularfelter', await this.c.kraevKlient(), 'id', id);
  private async raaIndstillinger(k: string): Promise<{ data: R; navn: string }> {
    const [i, kl] = await Promise.all([
      this.c.k(this.c.sb.from('indstillinger').select('data').eq('klient_id', k).maybeSingle()),
      this.c.k(this.c.sb.from('klienter').select('navn').eq('id', k).single()),
    ]);
    return { data: ((i as { data: R } | null)?.data ?? {}), navn: (kl as { navn: string }).navn };
  }
  indstillinger: Api['indstillinger'] = async () => {
    const { data, navn } = await this.raaIndstillinger(await this.c.kraevKlient());
    return fletIndstillinger(data, standardIndstillinger({ navn }));
  };
  /** Steamere må læse indstillingerne (RLS "indstillinger"), men kun dette udsnit bruges i steamer-appen. */
  steamerIndstillinger: Api['steamerIndstillinger'] = async () => {
    const k = await this.c.kraevKlient();
    const i = (await this.c.k(this.c.sb.from('indstillinger').select('data').eq('klient_id', k).maybeSingle())) as { data: R } | null;
    return fletIndstillinger(i?.data ?? {}).steamer;
  };
  gemIndstillinger: Api['gemIndstillinger'] = async (i: Partial<Indstillinger>) => {
    const k = await this.c.kraevKlient();
    const { data, navn } = await this.raaIndstillinger(k);
    const ny = O.tjekIndstillinger({ ...fletIndstillinger(data, standardIndstillinger({ navn })), ...structuredClone(i) });
    // Det, der ikke er en indstilling (fx rollernes rettigheder), bevares uændret
    await this.c.k(this.c.sb.from('indstillinger').upsert({ klient_id: k, data: { ...data, ...ny } }, { onConflict: 'klient_id' }));
    this.c.skift();
  };
  /** Rollernes rettigheder – ejerens tilpasning oven på standarden. */
  rolleEvner: Api['rolleEvner'] = async () => {
    const m = (await this.rpc('rolle_evner', { k: await this.c.kraevKlient() })) as Partial<RolleEvner> | null;
    return { admin: STANDARD_ROLLE_EVNER.admin, kundeservice: m?.kundeservice ?? STANDARD_ROLLE_EVNER.kundeservice, saelger: m?.saelger ?? STANDARD_ROLLE_EVNER.saelger };
  };
  gemRolleEvner: Api['gemRolleEvner'] = async (m: RolleEvner) => {
    const k = await this.c.kraevKlient();
    const { data } = await this.raaIndstillinger(k);
    const ny = { kundeservice: [...new Set(m.kundeservice)], saelger: [...new Set(m.saelger ?? STANDARD_ROLLE_EVNER.saelger)].filter((e) => SAELGER_KAN.includes(e)) };
    await this.c.k(this.c.sb.from('indstillinger').upsert({ klient_id: k, data: { ...data, rolleEvner: ny } }, { onConflict: 'klient_id' }));
    this.c.skift();
  };
  lukkedage: Api['lukkedage'] = async () => (await this.liste('lukkedage', 'dato')).map(fraDb.lukkedag);
  gemLukkedag: Api['gemLukkedag'] = async (l: Lukkedag) => {
    const k = await this.c.kraevKlient();
    await this.c.k(this.c.sb.from('lukkedage').upsert({ ...O.normLukkedag(l), klient_id: k }, { onConflict: 'klient_id,dato' }));
    this.c.skift();
  };
  sletLukkedag: Api['sletLukkedag'] = async (dato: string) => this.slet('lukkedage', await this.c.kraevKlient(), 'dato', dato);
  tilfoejHelligdage: Api['tilfoejHelligdage'] = async (aar: number) => {
    const k = await this.c.kraevKlient();
    const har = new Set((await this.liste('lukkedage')).map((r) => String(r.dato)));
    const nye = helligdage(aar).filter((h) => !har.has(h.dato));
    if (nye.length) await this.c.k(this.c.sb.from('lukkedage').insert(nye.map((h) => ({ ...h, klient_id: k }))));
    this.c.skift();
    return nye.length;
  };

  // ---------- Steamere ----------
  steamere: Api['steamere'] = async () => {
    // Skjulte steamere (pladsholdere fra LatePoint som bynavne og "Gavekort" – 20261030171000_lp_pladsholdere.sql) vises ikke
    const synlige = async () => (await this.c.k(this.c.sb.from('steamere').select(KOLONNER.steamere).eq('klient_id', await this.c.kraevKlient()).eq('skjult', false).order('navn'))) as unknown as R[];
    const [s, a, f, d] = await Promise.all([synlige(), this.liste('arbejdstider'), this.liste('fravaer', 'fra'), this.liste('saerlige_dage', 'dato')]);
    return { steamere: s.map(fraDb.steamer), arbejdstider: a.map(fraDb.arbejdstid), fravaer: f.map(fraDb.fravaer), saerligeDage: d.map(fraDb.saerligDag) };
  };
  gemSteamer: Api['gemSteamer'] = async (s: Steamer, tider: Arbejdstid[]) => {
    const n = O.normSteamer(s, tider);
    await this.rpc('gem_steamer', { k: await this.c.kraevKlient(), s: { ...tilDb.steamer(n.steamer), maxOpgaver: n.steamer.maxOpgaver ?? null }, tider: n.tider.map(({ ugedag, start, slut }) => ({ ugedag, start, slut })) });
    this.c.skift();
  };
  gemFastFri: Api['gemFastFri'] = async (steamer_id, fri) => {
    await this.rpc('gem_fast_fri', { k: await this.c.kraevKlient(), p_steamer: steamer_id, p_fri: normFastFri(fri) });
    this.c.skift();
  };
  steamerNoegletal: Api['steamerNoegletal'] = async (steamer_id: string, fra: string) => {
    const k = await this.c.kraevKlient();
    const antal = async (q: PromiseLike<{ count: number | null; error: unknown }>) => { const r = await q; if (r.error) throw new ApiFejl('Tallene for steameren kunne ikke hentes. Prøv igen.'); return r.count ?? 0; };
    const bk = () => this.c.sb.from('bookinger').select('id', { count: 'exact', head: true }).eq('klient_id', k).gte('dato', fra);
    const rek = await this.c.k(this.c.sb.from('bookinger').select('reklamation_for').eq('klient_id', k).eq('kilde', 'reklamation').gte('dato', fra).not('reklamation_for', 'is', null)) as { reklamation_for: number }[];
    const [opgaver, reklamationer] = await Promise.all([
      antal(bk().eq('steamer_id', steamer_id).eq('status', 'faerdig')),
      rek.length ? antal(this.c.sb.from('bookinger').select('id', { count: 'exact', head: true }).eq('klient_id', k).eq('steamer_id', steamer_id).in('id', rek.map((x) => x.reklamation_for))) : Promise.resolve(0),
    ]);
    return { opgaver, reklamationer };
  };
  gemSaerligDag: Api['gemSaerligDag'] = async (d: Omit<SaerligDag, 'id'>) => {
    const n = O.normSaerligDag(d);
    await this.rpc('gem_saerlig_dag', { k: await this.c.kraevKlient(), p_steamer: n.steamer_id, p_dato: n.dato, p_start: n.start, p_slut: n.slut });
    this.c.skift();
  };
  sletSaerligDag: Api['sletSaerligDag'] = async (id: string) => this.slet('saerlige_dage', await this.c.kraevKlient(), 'id', id);
  meldFravaer: Api['meldFravaer'] = async (f: Omit<Fravaer, 'id'>) => {
    const n = O.normFravaer(f);
    const ramte = await this.rpc('meld_fravaer', { k: await this.c.kraevKlient(), p_steamer: n.steamer_id, p_fra: n.fra, p_til: n.til, p_type: n.type, p_note: n.note ?? '' });
    this.c.skift();
    return { ramte: Number(ramte) || 0 };
  };
  sletFravaer: Api['sletFravaer'] = async (id: string) => this.slet('fravaer', await this.c.kraevKlient(), 'id', id);
  // Steameren melder selv fravær som en anmodning; kontoret godkender eller afviser (20261028070000_fravaer_anmodning.sql)
  mitFravaer: Api['mitFravaer'] = async () => {
    const [f, a] = await Promise.all([this.liste('fravaer', 'fra'), this.liste('fravaer_anmodninger', 'fra')]);
    return { fravaer: f.map(fraDb.fravaer), anmodninger: a.map(fraDb.fravaerAnmodning) };
  };
  anmodFravaer: Api['anmodFravaer'] = async (f) => {
    const n = O.normFravaer({ ...f, steamer_id: '' });
    if (n.fra < iDag()) throw new ApiFejl('Du kan ikke melde fravær bagud i tid.', { fra: 'Vælg i dag eller senere' });
    const id = await this.rpc('anmod_fravaer', { k: await this.c.kraevKlient(), p_fra: n.fra, p_til: n.til, p_type: n.type, p_note: n.note ?? '' });
    this.c.skift();
    return { id: String(id) };
  };
  fravaerAnmodninger: Api['fravaerAnmodninger'] = async () => {
    const l = (await this.rpc('fravaer_anmodninger', { k: await this.c.kraevKlient() })) as (R & { navn: string; opgaver: number })[] | null;
    return (l ?? []).map((r) => ({ ...fraDb.fravaerAnmodning(r), navn: String(r.navn ?? ''), opgaver: Number(r.opgaver) || 0 })) as (FravaerAnmodning & { navn: string; opgaver: number })[];
  };
  svarFravaer: Api['svarFravaer'] = async (id, godkend, grund) => {
    const g = rens(grund ?? '', 200);
    if (!godkend && g.length < 2) throw new ApiFejl('Skriv hvorfor du afviser.', { grund: 'Skriv en kort grund' });
    const ramte = await this.rpc('svar_fravaer', { k: await this.c.kraevKlient(), p_id: id, p_godkend: godkend, p_grund: g });
    this.c.skift();
    return { ramte: Number(ramte) || 0 };
  };
  // Fast fri som anmodning fra steamer-appen (20261029890000_fastfri_anmod.sql). RLS: steameren ser kun sig selv.
  mitFastFri: Api['mitFastFri'] = async () => {
    const [s, a] = await Promise.all([this.liste('steamere'), this.liste('fastfri_anmodninger', 'oprettet')]);
    return { fastFri: (s[0]?.fast_fri as FastFri[] | null) ?? [], anmodninger: a.map(fraDb.fastfriAnmodning).reverse() };
  };
  anmodFastFri: Api['anmodFastFri'] = async (f) => {
    const [n] = normFastFri([f]);
    const id = await this.rpc('anmod_fast_fri', { k: await this.c.kraevKlient(), p_ugedag: n.ugedag, p_fra: n.fra, p_til: n.til, p_note: n.note ?? '' });
    this.c.skift();
    return { id: String(id) };
  };
  fastFriAnmodninger: Api['fastFriAnmodninger'] = async () => {
    const l = (await this.rpc('fastfri_anmodninger', { k: await this.c.kraevKlient() })) as (R & { navn: string })[] | null;
    return (l ?? []).map((r) => ({ ...fraDb.fastfriAnmodning(r), navn: String(r.navn ?? '') })) as (FastFriAnmodning & { navn: string })[];
  };
  svarFastFri: Api['svarFastFri'] = async (id, godkend, grund) => {
    await this.rpc('svar_fast_fri', { k: await this.c.kraevKlient(), p_id: id, p_godkend: godkend, p_grund: rens(grund ?? '', 200) });
    this.c.skift();
  };

  // ---------- Subdomæner (brands) ----------
  subdomaener: Api['subdomaener'] = async () => {
    const l = ((await this.rpc('subdomaener', { k: await this.c.kraevKlient() })) as (Partial<SubBrand> & { id: string; navn: string; bookinger: number })[] | null) ?? [];
    return l.map((b) => { const t = TOM_BRAND(b.navn); return { ...t, ...b, design: { ...t.design, ...b.design }, kontakt: { ...t.kontakt, ...b.kontakt },
      afsender: { ...t.afsender, ...b.afsender }, tekster: { ...t.tekster, ...b.tekster }, ydelser: b.ydelser ?? [], bookinger: Number(b.bookinger) || 0 }; });
  };
  gemSubdomaene: Api['gemSubdomaene'] = async (b, ny) => {
    const k = await this.c.kraevKlient();
    const ydelser = ((await this.c.k(this.c.sb.from('ydelser').select('id').eq('klient_id', k))) as { id: string }[]).map((y) => y.id);
    const n = normBrand(b, ydelser);
    if (n.domaene) {
      const { data } = await this.c.sb.from('brands').select('id').eq('klient_id', k).eq('domaene', n.domaene).neq('id', n.id).limit(1);
      if (data?.length) throw new ApiFejl('Domænet bruges allerede af et andet subdomæne.', { domaene: 'Bruges allerede.' });
    }
    const raekke = { klient_id: k, ...n };
    if (ny) {
      const { error } = await this.c.sb.from('brands').insert(raekke);
      if (error?.code === '23505') throw new ApiFejl('Der findes allerede et subdomæne med det id.', { id: 'Findes allerede.' });
      if (error) await this.c.k(Promise.resolve({ data: null, error }));
    } else await this.c.k(this.c.sb.from('brands').update(raekke).eq('klient_id', k).eq('id', n.id));
    this.c.skift();
  };
  sletSubdomaene: Api['sletSubdomaene'] = async (id) => {
    const k = await this.c.kraevKlient();
    const { count } = await this.c.sb.from('bookinger').select('id', { count: 'exact', head: true }).eq('klient_id', k).eq('brand_id', id);
    if (count) throw new ApiFejl('Der er bookinger fra subdomænet. Slå det fra i stedet for at slette det.');
    await this.c.k(this.c.sb.from('brands').delete().eq('klient_id', k).eq('id', id));
    this.c.skift();
  };

  // ---------- Partneraftaler ----------
  partnere: Api['partnere'] = async () => {
    const l = ((await this.rpc('partnere_oversigt', { k: await this.c.kraevKlient() })) as R[] | null) ?? [];
    return l.map((p) => ({ ...fraDb.partner(p), pakker: ((p.pakker as R[]) ?? []).map(fraDb.partnerpakke), bookinger: Number(p.bookinger) || 0,
      omsaetning: Number(p.omsaetning) || 0, seneste: (p.seneste as string) ?? null }));
  };
  gemPartner: Api['gemPartner'] = async (p: Partner, pakker: PartnerPakke[]) => {
    const k = await this.c.kraevKlient();
    const [ydelser, biltyper, brands] = await Promise.all((['ydelser', 'biltyper', 'brands'] as const).map(async (t) =>
      ((await this.c.k(this.c.sb.from(t).select('id').eq('klient_id', k))) as { id: string }[])));
    const n = normPartner(p, pakker, { ydelser, biltyper, brands: brands.map((b) => b.id) });
    const id = (await this.rpc('gem_partner', { k, p: { ...n.partner, id: p.id || null, pakker: n.pakker } })) as string;
    this.c.skift();
    return id;
  };
  sletPartner: Api['sletPartner'] = async (id) => {
    await this.rpc('slet_partner', { k: await this.c.kraevKlient(), p_id: id });
    this.c.skift();
  };
}
